// src/components/DisponibilidadConfig.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
  Switch,
  FormControlLabel,
  Tooltip,
  Card,
  CardContent,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Block as BlockIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { toast } from 'react-toastify';
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../axiosInstance';

// Días en backend: 0=Lunes ... 6=Domingo
const diasSemana = [
  { value: 0, label: 'Lunes' },
  { value: 1, label: 'Martes' },
  { value: 2, label: 'Miércoles' },
  { value: 3, label: 'Jueves' },
  { value: 4, label: 'Viernes' },
  { value: 5, label: 'Sábado' },
  { value: 6, label: 'Domingo' }
];

// Utilidad: formatear Date a "HH:MM:SS"
const timeToHHMMSS = (dateObj) => {
  if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) return null;
  const hh = String(dateObj.getHours()).padStart(2, '0');
  const mm = String(dateObj.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}:00`;
};

// Utilidad: parse "HH:MM:SS" a Date (día fijo)
const HHMMSSToDate = (hhmmss) => {
  if (!hhmmss) return null;
  const [h, m, s] = hhmmss.split(':').map(Number);
  const d = new Date(2000, 0, 1, h || 0, m || 0, s || 0, 0);
  return d;
};

// Utilidad: formatear "HH:MM:SS" a string legible según use12h
const formatTime = (hhmmss, use12h) => {
  if (!hhmmss) return '';
  const d = HHMMSSToDate(hhmmss);
  if (!d) return '';
  
  const hh = d.getHours();
  const mm = String(d.getMinutes()).padStart(2, '0');
  
  if (use12h) {
    const suffix = hh >= 12 ? 'PM' : 'AM';
    const h12 = hh % 12 || 12;
    return `${h12}:${mm} ${suffix}`;
  }
  return `${String(hh).padStart(2, '0')}:${mm}`;
};

// UI: tarjeta de bloques de excepciones por día (CONECTADA AL BACKEND)
function ExcepcionesDiaCard({
  use12h,
  excepciones,
  onAdd,
  onRemove,
  diaLabel,
  loading,
}) {
  const theme = useTheme();
  const [openForm, setOpenForm] = useState(false);
  const [inicio, setInicio] = useState(HHMMSSToDate('13:00:00'));
  const [fin, setFin] = useState(HHMMSSToDate('14:00:00'));
  const [motivo, setMotivo] = useState('');
  const [saving, setSaving] = useState(false);

  const handleAdd = async () => {
    if (!inicio || !fin) {
      toast.warning('Selecciona horas de inicio y fin');
      return;
    }
    if (inicio >= fin) {
      toast.warning('La hora de fin debe ser posterior a la de inicio');
      return;
    }

    const nuevo = {
      hora_inicio: timeToHHMMSS(inicio),
      hora_fin: timeToHHMMSS(fin),
      motivo: motivo.trim() || null,
    };

    setSaving(true);
    try {
      await onAdd(nuevo);
      setMotivo('');
      setOpenForm(false);
    } catch (err) {
      // onAdd ya muestra el toast de error
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        borderColor: alpha(theme.palette.error.main, 0.25),
        bgcolor: alpha(theme.palette.error.main, 0.03)
      }}
    >
      <CardContent sx={{ pt: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={1.5}
          sx={{ mb: 1 }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <BlockIcon color="error" fontSize="small" />
            <Typography variant="subtitle1" fontWeight={600}>
              Bloques no disponibles
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1}>
            <Button
              variant={openForm ? 'outlined' : 'contained'}
              color="error"
              onClick={() => setOpenForm((v) => !v)}
              size="small"
              startIcon={openForm ? <CancelIcon /> : <AddIcon />}
              disabled={loading}
            >
              {openForm ? 'Cancelar' : 'Añadir bloque'}
            </Button>
          </Stack>
        </Stack>

        {openForm && (
          <Box
            sx={{
              p: 2,
              mb: 2,
              borderRadius: 2,
              border: `1px dashed ${alpha(theme.palette.error.main, 0.35)}`,
              bgcolor: 'background.paper'
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TimePicker
                  label="Desde"
                  value={inicio}
                  onChange={(v) => setInicio(v)}
                  ampm={use12h}
                  ampmInClock={use12h}
                  minutesStep={5}
                  viewRenderers={{
                    hours: renderTimeViewClock,
                    minutes: renderTimeViewClock,
                    seconds: renderTimeViewClock,
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'medium',
                      sx: { '& .MuiInputBase-root': { height: 48 } }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TimePicker
                  label="Hasta"
                  value={fin}
                  onChange={(v) => setFin(v)}
                  ampm={use12h}
                  ampmInClock={use12h}
                  minutesStep={5}
                  viewRenderers={{
                    hours: renderTimeViewClock,
                    minutes: renderTimeViewClock,
                    seconds: renderTimeViewClock,
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'medium',
                      sx: { '& .MuiInputBase-root': { height: 48 } }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Motivo (opcional)"
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  inputProps={{ maxLength: 120 }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm="auto">
                <Button
                  variant="contained"
                  color="error"
                  startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                  onClick={handleAdd}
                  disabled={saving}
                  sx={{ height: 48 }}
                >
                  {saving ? 'Guardando...' : 'Guardar bloque'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : excepciones?.length ? (
          <Box sx={{ mt: 1 }}>
            <Stack spacing={1.5}>
              {excepciones.map((exc) => (
                <Paper
                  key={exc.id}
                  variant="outlined"
                  sx={{
                    p: 1.25,
                    borderRadius: 2,
                    borderColor: alpha(theme.palette.error.main, 0.2),
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        size="small"
                        color="error"
                        variant="outlined"
                        label={`🚫 ${formatTime(exc.hora_inicio, use12h)} - ${formatTime(exc.hora_fin, use12h)}`}
                        sx={{ fontWeight: 600 }}
                      />
                      {!!exc.motivo && (
                        <Typography variant="body2" color="text.secondary">
                          ({exc.motivo})
                        </Typography>
                      )}
                    </Stack>
                    <Tooltip title="Eliminar bloque">
                      <IconButton color="error" onClick={() => onRemove(exc.id)} size="small">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No hay bloques no disponibles definidos para este día.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

const DisponibilidadConfig = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [disponibilidades, setDisponibilidades] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Switch de formato de hora 12h/24h
  const [use12h, setUse12h] = useState(false);

  // Creación múltiple de días (ya existente)
  const [multiDayMode, setMultiDayMode] = useState(false);
  const [selectedDays, setSelectedDays] = useState([]);

  const [formData, setFormData] = useState({
    dia_semana: '',
    hora_inicio: null,
    hora_fin: null,
    intervalo_minutos: 30
  });

  const [formErrors, setFormErrors] = useState({});

  // NUEVO: bloques desde backend (BlockedSlot model)
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [loadingBlocks, setLoadingBlocks] = useState(false);

  const getThrottleMessage = (err, fallback = 'Has alcanzado el límite de solicitudes. Intenta nuevamente en unos segundos.') => {
    const hdr = err?.response?.headers?.['retry-after'];
    if (hdr) return `Has alcanzado el límite de solicitudes. Intenta nuevamente en ~${parseInt(hdr, 10)}s.`;
    const detail = err?.response?.data?.detail || '';
    const m = String(detail).match(/(\d+)\s*seg/);
    if (m?.[1]) return `Has alcanzado el límite de solicitudes. Intenta nuevamente en ~${m[1]}s.`;
    return fallback;
  };

  // Cargar disponibilidades + bloques al montar
  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const fetchData = async () => {
      try {
        setLoading(true);
        setLoadingBlocks(true);

        const [dispResp, blocksResp] = await Promise.all([
          axiosInstance.get('/disponibilidad/', { signal }),
          axiosInstance.get('/blocked-slots/', { signal }).catch(() => ({ data: [] }))
        ]);

        setDisponibilidades(dispResp.data || []);
        setBlockedSlots(blocksResp.data || []);
      } catch (error) {
        if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
        console.error('Error fetching data:', error);
        if (error?.response?.status === 429) {
          toast.warning(getThrottleMessage(error));
        } else {
          toast.error('Error al cargar las configuraciones');
        }
      } finally {
        setLoading(false);
        setLoadingBlocks(false);
      }
    };

    fetchData();
    return () => controller.abort();
  }, []);

  const handleOpenDialog = (disponibilidad = null) => {
    if (disponibilidad) {
      setEditingId(disponibilidad.id);
      setMultiDayMode(false);
      setSelectedDays([]);
      setFormData({
        dia_semana: disponibilidad.dia_semana,
        hora_inicio: HHMMSSToDate(disponibilidad.hora_inicio),
        hora_fin: HHMMSSToDate(disponibilidad.hora_fin),
        intervalo_minutos: disponibilidad.intervalo_minutos
      });
    } else {
      setEditingId(null);
      setFormData({
        dia_semana: '',
        hora_inicio: null,
        hora_fin: null,
        intervalo_minutos: 30
      });
      setSelectedDays([]);
    }
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingId(null);
    setFormData({
      dia_semana: '',
      hora_inicio: null,
      hora_fin: null,
      intervalo_minutos: 30
    });
    setSelectedDays([]);
    setFormErrors({});
  };

  const validateForm = () => {
    const errors = {};

    if (!editingId) {
      if (multiDayMode) {
        if (!selectedDays || selectedDays.length === 0) {
          errors.dias = 'Selecciona al menos un día';
        }
      } else {
        if (formData.dia_semana === '') {
          errors.dia_semana = 'Selecciona un día de la semana';
        }
      }
    } else {
      if (formData.dia_semana === '') {
        errors.dia_semana = 'Selecciona un día de la semana';
      }
    }

    if (!formData.hora_inicio) {
      errors.hora_inicio = 'Selecciona una hora de inicio';
    }
    if (!formData.hora_fin) {
      errors.hora_fin = 'Selecciona una hora de fin';
    }
    if (formData.hora_inicio && formData.hora_fin) {
      if (formData.hora_inicio >= formData.hora_fin) {
        errors.hora_fin = 'La hora de fin debe ser posterior a la hora de inicio';
      }
    }

    if (!formData.intervalo_minutos || formData.intervalo_minutos < 5) {
      errors.intervalo_minutos = 'El intervalo debe ser de al menos 5 minutos';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);

    try {
      const payloadBase = {
        hora_inicio: timeToHHMMSS(formData.hora_inicio),
        hora_fin: timeToHHMMSS(formData.hora_fin),
        intervalo_minutos: formData.intervalo_minutos
      };

      if (editingId) {
        // Actualizar existente
        await axiosInstance.put(
          `/disponibilidad/${editingId}/`,
          { ...payloadBase, dia_semana: formData.dia_semana }
        );
        toast.success('Disponibilidad actualizada correctamente');
      } else {
        // Crear nuevas
        if (multiDayMode && selectedDays.length > 0) {
          const requests = selectedDays.map((day) =>
            axiosInstance.post(
              '/disponibilidad/',
              { ...payloadBase, dia_semana: day }
            ).catch((error) => ({ error }))
          );
          const results = await Promise.all(requests);

          let created = 0;
          let duplicates = 0;
          let failed = 0;

          results.forEach((res) => {
            if (res && res.error) {
              const status = res.error?.response?.status;
              if (status === 400) {
                duplicates += 1;
              } else if (status === 429) {
                toast.warning(getThrottleMessage(res.error));
                failed += 1;
              } else {
                failed += 1;
              }
            } else {
              created += 1;
            }
          });

          if (created > 0) toast.success(`Se crearon ${created} disponibilidades`);
          if (duplicates > 0) toast.info(`${duplicates} día(s) ya tenían disponibilidad configurada`);
          if (failed > 0) toast.error(`${failed} disponibilidad(es) no pudieron crearse`);
        } else {
          await axiosInstance.post(
            '/disponibilidad/',
            { ...payloadBase, dia_semana: formData.dia_semana }
          );
          toast.success('Disponibilidad creada correctamente');
        }
      }

      handleCloseDialog();
      // refrescar listado
      setLoading(true);
      const resp = await axiosInstance.get('/disponibilidad/');
      setDisponibilidades(resp.data || []);
    } catch (error) {
      console.error('Error saving disponibilidad:', error?.response?.data || error);
      const data = error?.response?.data;

      if (error?.response?.status === 429) {
        toast.error(getThrottleMessage(error));
      } else {
        const msg =
          (typeof data === 'string' && data) ||
          data?.detail ||
          data?.dia_semana ||
          data?.error ||
          'Error al guardar la disponibilidad';
        toast.error(Array.isArray(msg) ? msg.join(', ') : msg);
      }
    } finally {
      setSaving(false);
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta disponibilidad?')) {
      return;
    }

    try {
      await axiosInstance.delete(`/disponibilidad/${id}/`);
      toast.success('Disponibilidad eliminada correctamente');
      // refrescar listado
      setLoading(true);
      const resp = await axiosInstance.get('/disponibilidad/');
      setDisponibilidades(resp.data || []);
    } catch (error) {
      console.error('Error deleting disponibilidad:', error);
      if (error?.response?.status === 429) {
        toast.error(getThrottleMessage(error));
      } else {
        toast.error('Error al eliminar la disponibilidad');
      }
    } finally {
      setLoading(false);
    }
  };

  // ========== BLOQUES NO DISPONIBLES (BACKEND) ==========
  const getBlocksForDay = (dia_semana) => {
    return blockedSlots.filter((b) => b.dia_semana === dia_semana && !b.fecha);
  };

  const handleAddBlock = async (dia_semana, payload) => {
    try {
      const newBlock = await axiosInstance.post('/blocked-slots/', {
        dia_semana,
        ...payload
      });
      setBlockedSlots((prev) => [...prev, newBlock.data]);
      toast.success('Bloque añadido correctamente');
    } catch (error) {
      console.error('Error adding block:', error);
      if (error?.response?.status === 429) {
        toast.error(getThrottleMessage(error));
      } else {
        const msg = error?.response?.data?.detail || 'Error al añadir bloque';
        toast.error(msg);
      }
      throw error;
    }
  };

  const handleRemoveBlock = async (blockId) => {
    if (!window.confirm('¿Eliminar este bloque?')) return;

    try {
      await axiosInstance.delete(`/blocked-slots/${blockId}/`);
      setBlockedSlots((prev) => prev.filter((b) => b.id !== blockId));
      toast.success('Bloque eliminado');
    } catch (error) {
      console.error('Error removing block:', error);
      if (error?.response?.status === 429) {
        toast.error(getThrottleMessage(error));
      } else {
        toast.error('Error al eliminar bloque');
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          p: 3,
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.divider, 0.8)}`
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            mb: 3,
            p: 2,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <IconButton
              aria-label="Volver"
              onClick={() => navigate(-1)}
              sx={{ borderRadius: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h5" gutterBottom fontWeight="bold">
                Configuración de Disponibilidad
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Define los días y horarios en los que estarás disponible y bloquea franjas específicas (almuerzos, reuniones, etc.)
              </Typography>
            </Box>
          </Box>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={use12h}
                  onChange={(e) => setUse12h(e.target.checked)}
                />
              }
              label="Formato 12h (AM/PM)"
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Añadir Horario
            </Button>
          </Stack>
        </Stack>

        {disponibilidades.length === 0 ? (
          <Alert severity="info">
            No has configurado ningún horario disponible. Añade tu disponibilidad para que los pacientes puedan agendar citas.
          </Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Día</strong></TableCell>
                  <TableCell><strong>Horario</strong></TableCell>
                  <TableCell><strong>Intervalo</strong></TableCell>
                  <TableCell><strong>Bloques no disponibles</strong></TableCell>
                  <TableCell align="right"><strong>Acciones</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[...disponibilidades]
                  .sort((a, b) => a.dia_semana - b.dia_semana)
                  .map((disp) => {
                    const dayBlocks = getBlocksForDay(disp.dia_semana);
                    const dayLabel = diasSemana.find(d => d.value === disp.dia_semana)?.label || `Día ${disp.dia_semana}`;
                    return (
                      <TableRow key={disp.id}>
                        <TableCell>
                          <Chip
                            label={dayLabel}
                            color="primary"
                            variant="outlined"
                            sx={{ borderRadius: 2 }}
                          />
                        </TableCell>
                        <TableCell>
                          {formatTime(disp.hora_inicio, use12h)} - {formatTime(disp.hora_fin, use12h)}
                        </TableCell>
                        <TableCell>
                          {disp.intervalo_minutos} minutos
                        </TableCell>
                        <TableCell sx={{ minWidth: 340 }}>
                          <ExcepcionesDiaCard
                            use12h={use12h}
                            excepciones={dayBlocks}
                            onAdd={(payload) => handleAddBlock(disp.dia_semana, payload)}
                            onRemove={handleRemoveBlock}
                            diaLabel={dayLabel}
                            loading={loadingBlocks}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Editar disponibilidad">
                            <IconButton
                              color="primary"
                              onClick={() => handleOpenDialog(disp)}
                              size="small"
                              sx={{ mr: 0.5 }}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar disponibilidad">
                            <IconButton
                              color="error"
                              onClick={() => handleDelete(disp.id)}
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Diálogo para añadir/editar disponibilidad */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingId ? 'Editar Disponibilidad' : 'Añadir Disponibilidad'}
          </DialogTitle>
          <DialogContent>
            {!editingId && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1, mb: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={multiDayMode}
                      onChange={(e) => {
                        setMultiDayMode(e.target.checked);
                        setSelectedDays([]);
                        setFormErrors((prev) => ({ ...prev, dias: '' }));
                      }}
                    />
                  }
                  label="Seleccionar múltiples días"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={use12h}
                      onChange={(e) => setUse12h(e.target.checked)}
                    />
                  }
                  label="Formato 12h (AM/PM)"
                />
              </Stack>
            )}

            <Grid container spacing={3} sx={{ mt: 1 }}>
              {/* Día(s) */}
              <Grid item xs={12}>
                {editingId || !multiDayMode ? (
                  <FormControl
                    fullWidth
                    error={!!formErrors.dia_semana}
                    sx={{
                      minWidth: { xs: '100%', sm: 560 },
                      '& .MuiOutlinedInput-root': { height: 48 }
                    }}
                  >
                    <InputLabel>Día de la semana</InputLabel>
                    <Select
                      value={formData.dia_semana}
                      label="Día de la semana"
                      onChange={(e) => setFormData({ ...formData, dia_semana: e.target.value })}
                    >
                      {diasSemana.map((dia) => (
                        <MenuItem key={dia.value} value={dia.value}>
                          {dia.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {formErrors.dia_semana && (
                      <Typography variant="caption" color="error">
                        {formErrors.dia_semana}
                      </Typography>
                    )}
                  </FormControl>
                ) : (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Selecciona uno o más días:
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {diasSemana.map((dia) => {
                        const selected = selectedDays.includes(dia.value);
                        return (
                          <Chip
                            key={dia.value}
                            label={dia.label}
                            clickable
                            color={selected ? 'primary' : 'default'}
                            variant={selected ? 'filled' : 'outlined'}
                            onClick={() => {
                              setSelectedDays((prev) =>
                                                                prev.includes(dia.value)
                                  ? prev.filter((v) => v !== dia.value)
                                  : [...prev, dia.value]
                              );
                              setFormErrors((prev) => ({ ...prev, dias: '' }));
                            }}
                            sx={{ borderRadius: 2, mb: 1 }}
                          />
                        );
                      })}
                    </Stack>
                    {formErrors.dias && (
                      <Typography variant="caption" color="error">
                        {formErrors.dias}
                      </Typography>
                    )}
                  </Box>
                )}
              </Grid>

              {/* Hora inicio */}
              <Grid item xs={12} sm={6}>
                <TimePicker
                  label="Hora de inicio"
                  value={formData.hora_inicio}
                  onChange={(newValue) => setFormData({ ...formData, hora_inicio: newValue })}
                  ampm={use12h}
                  ampmInClock={use12h}
                  minutesStep={5}
                  viewRenderers={{
                    hours: renderTimeViewClock,
                    minutes: renderTimeViewClock,
                    seconds: renderTimeViewClock,
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'medium',
                      error: !!formErrors.hora_inicio,
                      helperText: formErrors.hora_inicio,
                      sx: {
                        '& .MuiInputBase-root': { height: 48 }
                      }
                    }
                  }}
                />
              </Grid>

              {/* Hora fin */}
              <Grid item xs={12} sm={6}>
                <TimePicker
                  label="Hora de fin"
                  value={formData.hora_fin}
                  onChange={(newValue) => setFormData({ ...formData, hora_fin: newValue })}
                  ampm={use12h}
                  ampmInClock={use12h}
                  minutesStep={5}
                  viewRenderers={{
                    hours: renderTimeViewClock,
                    minutes: renderTimeViewClock,
                    seconds: renderTimeViewClock,
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'medium',
                      error: !!formErrors.hora_fin,
                      helperText: formErrors.hora_fin,
                      sx: {
                        '& .MuiInputBase-root': { height: 48 }
                      }
                    }
                  }}
                />
              </Grid>

              {/* Intervalo */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Intervalo entre citas (minutos)"
                  type="number"
                  value={formData.intervalo_minutos}
                  onChange={(e) =>
                    setFormData({ ...formData, intervalo_minutos: parseInt(e.target.value || '0', 10) })
                  }
                  error={!!formErrors.intervalo_minutos}
                  helperText={formErrors.intervalo_minutos || 'Duración de cada slot de tiempo disponible'}
                  InputProps={{ inputProps: { min: 5, max: 120, step: 5 } }}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseDialog} disabled={saving}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            >
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </LocalizationProvider>
  );
};

export default DisponibilidadConfig;

